import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  MessageSquare,
  Ticket,
  BellOff,
  LifeBuoy,
  Download,
} from "lucide-react";
import { AddCommentDialog } from "@/components/security/AddCommentDialog";
import { RunActionDropdown } from "@/components/security/RunActionDropdown";
import { CreateTicketDialog } from "@/components/security/CreateTicketDialog";
import { SuppressIssueDialog } from "@/components/security/SuppressIssueDialog";
import { downloadFile } from "@/utils/fileExport";
import type { SecurityIssue } from "@/types/security";

interface IssueActionBarProps {
  issue: SecurityIssue;
  numericIssueId: number;
}

export function IssueActionBar({ issue, numericIssueId }: IssueActionBarProps) {
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [suppressDialogOpen, setSuppressDialogOpen] = useState(false);

  const handleComment = () => {
    setCommentDialogOpen(true);
  };

  const handleCreateTicket = () => {
    setTicketDialogOpen(true);
  };

  const handleSupport = () => {
    if (window.$crisp) {
      window.$crisp.push(["set", "session:data", [
        ["issue_id", issue.id],
        ["issue_severity", issue.severity],
        ["issue_status", issue.status],
        ["issue_type", issue.type],
      ]]);
      window.$crisp.push(["do", "chat:open"]);
    } else {
      window.open(
        "mailto:support@vortal.so?subject=Support Request: " +
          encodeURIComponent(issue.id || "Security Issue"),
        "_blank"
      );
    }
  };

  const handleExportReport = () => {
    downloadFile({
      content: JSON.stringify({ issue, exportedAt: new Date().toISOString() }, null, 2),
      filename: `${issue.id}-report.json`,
      mimeType: "application/json",
    });
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="gap-2" onClick={handleComment}>
          <MessageSquare className="h-4 w-4" />
          Comment
        </Button>
        <RunActionDropdown issueId={numericIssueId} />
        <Button variant="outline" size="sm" className="gap-2" onClick={handleCreateTicket}>
          <Ticket className="h-4 w-4" />
          Create a Ticket
        </Button>
        {issue.status !== "SUPPRESSED" && (
          <Button variant="outline" size="sm" className="gap-2" onClick={() => setSuppressDialogOpen(true)}>
            <BellOff className="h-4 w-4" />
            Suppress
          </Button>
        )}
        <Button variant="outline" size="sm" className="gap-2" onClick={handleSupport}>
          <LifeBuoy className="h-4 w-4" />
          Support
        </Button>
        <Button variant="outline" size="sm" className="gap-2 ml-auto" onClick={handleExportReport}>
          <Download className="h-4 w-4" />
          Export Report
        </Button>
      </div>

      <AddCommentDialog
        open={commentDialogOpen}
        onOpenChange={setCommentDialogOpen}
        issueId={numericIssueId}
      />

      <CreateTicketDialog
        open={ticketDialogOpen}
        onOpenChange={setTicketDialogOpen}
        issueId={numericIssueId}
        issue={issue}
      />

      <SuppressIssueDialog
        open={suppressDialogOpen}
        onOpenChange={setSuppressDialogOpen}
        issue={issue}
        numericIssueId={numericIssueId}
      />
    </>
  );
}
